import React, { useEffect, useState } from "react";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { object, string, boolean } from "yup";
import { Formik } from "formik";
import { API } from "../../../api/API";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Button from "@mui/material/Button";
import { Box, Typography } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import Divider from "@mui/material/Divider";
import { appColors } from "../../../constant/constant";
import { PARTENAIRE_TYPES } from "../../../constant/GlobalConstant";

const RegisterPartenaire = ({ onRefresh }) => {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  // Schéma de validation adapté aux attributs de partenaire
  const partenaireSchema = object({
    intitule: string().required("L'intitulé est obligatoire"),
    is_active: boolean(),
    email: string().email("Email invalide").required("L'email est obligatoire"),
    tel: string()
      .required("Le téléphone est obligatoire")
      .matches(/^[0-9]+$/, "Doit contenir uniquement des chiffres")
      .min(8, "Doit contenir au moins 8 chiffres"),
    adresse: string().required("L'adresse est obligatoire"),
    ifu: string().required("L'IFU est obligatoire"),
    rccm: string().required("Le RCCM est obligatoire"),
    type: string().required("Le type est obligatoire"),
    /** Informations du responsable */
    responsable_nom: string().required("Le nom du responsable est obligatoire"),
    responsable_prenom: string().required(
      "Le prénom du responsable est obligatoire"
    ),
    responsable_tel: string()
      .required("Le téléphone du responsable est obligatoire")
      .matches(/^[0-9]+$/, "Doit contenir uniquement des chiffres")
      .min(8, "Doit contenir au moins 8 chiffres"),
    responsable_email: string()
      .email("Email du responsable invalide")
      .required("L'email du responsable est obligatoire"),
  });

  return (
    <>
      <ToastContainer position="top-right" autoClose={5000} />

      <Button
        variant="outlined"
        color="success"
        sx={{
          "&:hover": { backgroundColor: "#2e8b3a" },
          borderRadius: "8px",
          padding: "8px 16px",
          textTransform: "none",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
        onClick={handleShow}
      >
        <Typography variant="subtitle1">+ Partenaire</Typography>
      </Button>

      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-success text-light">
          <Modal.Title id="contained-modal-title-vcenter">
            <Typography variant="h6" component="h2" color="inherit">
              Enregistrement d'un nouveau partenaire
            </Typography>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Box sx={{ maxHeight: "70vh", overflowY: "auto", padding: "0 16px" }}>
            <Formik
              validationSchema={partenaireSchema}
              initialValues={{
                intitule: "",
                is_active: false,
                email: "",
                tel: "",
                adresse: "",
                ifu: "",
                rccm: "",

                type: "",
              }}
              onSubmit={async (values, { setSubmitting, resetForm }) => {
                const toastId = toast.loading("Enregistrement en cours...");
                try {
                  const resp = await API.post("/partenaires", values);
                  toast.update(toastId, {
                    render: "Partenaire créé avec succès!",
                    type: "success",
                    isLoading: false,
                    autoClose: 5000,
                  });

                  onRefresh();
                  /* handleClose();
                   resetForm();*/
                } catch (error) {
                  let errorMessage = "Erreur lors de la création du partenaire";

                  if (error.response) {
                    errorMessage =
                      error.response.data.message ||
                      `${error.response.status}: ${JSON.stringify(
                        error.response.data.error
                      )}`;
                  } else if (error.request) {
                    errorMessage = "Pas de réponse du serveur";
                  }

                  toast.update(toastId, {
                    render: errorMessage,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                  });
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }) => (
                <Form noValidate onSubmit={handleSubmit}>
                  <Divider sx={{ color: appColors.secondary }}>
                    Informations du partenaire
                  </Divider>

                  <Row className="mb-3">
                    <Form.Group as={Col} md={6} className="mb-3">
                      <Form.Label>
                        Intitulé <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        name="intitule"
                        type="text"
                        value={values.intitule}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Intitulé"
                        isInvalid={touched.intitule && !!errors.intitule}
                        className="rounded-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.intitule}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} md={6} className="mb-3">
                      <Form.Label>
                        Email <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        name="email"
                        type="email"
                        placeholder="Email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.email && !!errors.email}
                        className="rounded-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>

                  <Row className="mb-3">
                    <Form.Group as={Col} md={6} className="mb-3">
                      <Form.Label>
                        Téléphone <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        name="tel"
                        type="tel"
                        placeholder="Numéro de téléphone"
                        value={values.tel}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.tel && !!errors.tel}
                        className="rounded-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.tel}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} md={6} className="mb-3">
                      <Form.Label>
                        Adresse <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        name="adresse"
                        type="text"
                        placeholder="Adresse"
                        value={values.adresse}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.adresse && !!errors.adresse}
                        className="rounded-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.adresse}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>

                  <Row className="mb-3">
                    <Form.Group as={Col} md={6} className="mb-3">
                      <Form.Label>
                        IFU <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        name="ifu"
                        type="text"
                        placeholder="IFU"
                        value={values.ifu}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.ifu && !!errors.ifu}
                        className="rounded-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.ifu}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} md={6} className="mb-3">
                      <Form.Label>
                        RCCM <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        name="rccm"
                        type="text"
                        placeholder="RCCM"
                        value={values.rccm}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.rccm && !!errors.rccm}
                        className="rounded-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.rccm}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>

                  <Row className="mb-3">
                    <Form.Group as={Col} md={6} className="mb-3">
                      <Form.Label>
                        Type <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Select
                        name="type"
                        value={values.type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.type && !!errors.type}
                        className="rounded-input"
                      >
                        <option value="">Sélectionnez un type</option>
                        {PARTENAIRE_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.type}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Divider sx={{ color: appColors.secondary }}>
                    Informations du responsable
                  </Divider>
                  <Row className="mb-3">
                    <Form.Group as={Col} md={6} className="mb-3">
                      <Form.Label>
                        Nom du responsable{" "}
                        <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        name="responsable_nom"
                        type="text"
                        placeholder="Nom du responsable"
                        value={values.responsable_nom}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.responsable_nom && !!errors.responsable_nom
                        }
                        className="rounded-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.responsable_nom}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} md={6} className="mb-3">
                      <Form.Label>
                        Prénom du responsable{" "}
                        <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        name="responsable_prenom"
                        type="text"
                        placeholder="Prénom du responsable"
                        value={values.responsable_prenom}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.responsable_prenom &&
                          !!errors.responsable_prenom
                        }
                        className="rounded-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.responsable_prenom}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>

                  <Row className="mb-3">
                    <Form.Group as={Col} md={6} className="mb-3">
                      <Form.Label>
                        Téléphone du responsable{" "}
                        <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        name="responsable_tel"
                        type="tel"
                        placeholder="Téléphone du responsable"
                        value={values.responsable_tel}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.responsable_tel && !!errors.responsable_tel
                        }
                        className="rounded-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.responsable_tel}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} md={6} className="mb-3">
                      <Form.Label>
                        Email du responsable{" "}
                        <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        name="responsable_email"
                        type="email"
                        placeholder="Email du responsable"
                        value={values.responsable_email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.responsable_email &&
                          !!errors.responsable_email
                        }
                        className="rounded-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.responsable_email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      mt: 3,
                    }}
                  >
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleClose}
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "6px 16px",
                      }}
                    >
                      Annuler
                    </Button>

                    <Button
                      type="submit"
                      variant="contained"
                      color="success"
                      disabled={isSubmitting}
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "6px 24px",
                        backgroundColor: "#37B049",
                        "&:hover": { backgroundColor: "#2e8b3a" },
                      }}
                    >
                      {isSubmitting ? "Enregistrement..." : "Enregistrer"}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default RegisterPartenaire;
